import { Component, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { dateTimeFormat, dateTimeWithoutTimezone, monthOnlyFormat } from 'src/app/shared/utilities/date'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { DataTableComponent } from '../table/data-table.component'
import { ColDef, GetRowIdFunc, ICellRendererParams } from 'ag-grid-community'
import { catchError, combineLatest, firstValueFrom, from, NEVER, switchMap, tap } from 'rxjs'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { TableHeaderComponent } from '../table/headers/table-header.component'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { EntityFilterModalComponent } from '../modals/filters/entity-filter-modal.component'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { ToastService } from '../toasts/toasts.service'
import { MatMenuModule } from '@angular/material/menu'
import { TBillSettlementsService } from '../settlements/tbill-settlements.service'
import { PaginationParams } from 'src/app/shared/utilities/http-params'
import { TBillJobStatus } from '../settlements/tbill-settlements.types'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { TableSortService } from '../table/services/table-sort.service'
import { formatDuration } from 'src/app/shared/utilities/formatDuration'
import { DashboardService } from 'src/app/shared/services/dashboard.service'
import openModal from 'src/app/shared/services/openModal'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { actionColumn } from '../table/cells/with-cell'
import { DateCellComponent, DateCellComponentProps } from '../table/cells/date-cell.component'
import { EntityCellComponent, EntityCellComponentProps } from '../table/cells/entity-cell.component'
import { CreateSettlementRunModalComponent } from '../modals/create-settlement-run-modal.component'
import { StringFilter } from '../../shared/services/filter/string-filter'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { FilterMeta } from '../../shared/services/filter/common'
import { formatUser, getUserFilterConfig } from '../../shared/utilities/user'

const modalOptions: NgbModalOptions = {
  centered: true,
}

const headers: Readonly<Partial<Record<keyof TBillJobStatus, string>>> = {
  billing_month: 'Billing Month',
  operating_day: 'Operating Month',
  settlement_type: 'Settlement Type',
  run_status: 'Status',
  start_time: 'Start Time (STL)',
  end_time: 'End Time (STL)',
  duration: 'Duration',
  create_time: 'Create Time (STL)',
  created_by: 'Created By',
}

const getHeader = (field: keyof TBillJobStatus): string =>
  headers[field] ?? field

const getDateCellParams = (
  field: keyof TBillJobStatus,
  format: string,
  viewAsTZ: string,
): DateCellComponentProps<TBillJobStatus> => ({
  date: (status: TBillJobStatus) => status[field] as Date | null,
  format,
  viewAsTZ,
})

const dateColumn = (
  field: keyof TBillJobStatus,
  dateFormat: string,
  viewAsTZ = 'America/Chicago',
): ColDef => ({
  headerName: headers[field],
  field,
  headerComponentParams: {
    filterGroup,
    modal: DateRangeFilterModalComponent,
    modalOptions,
  },
  cellRenderer: DateCellComponent,
  cellRendererParams: getDateCellParams(field, dateFormat, viewAsTZ),
})

const filterGroup = 'TBillSettlementsComponent'

type EntityCellProps = EntityCellComponentProps<TBillJobStatus>

const getEntityCellParams = (field: keyof TBillJobStatus): EntityCellProps => ({
  value: (status: TBillJobStatus) => status[field] as ReturnType<EntityCellProps['value']>,
})

const entityColumn = (
  field: keyof TBillJobStatus,
  valueField: keyof TBillJobStatus = field,
): ColDef => ({
  headerName: headers[field],
  field,
  headerComponentParams: {
    filterGroup,
    modal: EntityFilterModalComponent,
    modalOptions,
  },
  cellRenderer: EntityCellComponent,
  cellRendererParams: getEntityCellParams(valueField),
})

const getMeta = (fieldName: keyof TBillJobStatus): FilterMeta => ({
  fieldGroup: filterGroup,
  fieldName,
  fieldDisplay: getHeader(fieldName),
})

@Component({
  selector: 'app-transmission-settlements',
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    TablePaginationComponent,
  ],
  providers: [TableSortService],
  template: `
    <div app-page-header header="TBill Settlement Runs" [menuItems]="pageHeaderMenuItems"></div>
    <div app-page-content>
        <app-active-filter-list [filterGroup]="filterGroup"/>
        <app-data-table
            heightStyle="calc(100vh - 320px)"
            [loading]="loading()"
            [data]="data()"
            [getRowId]="getRowId"
            [columnDefs]="columnDefs"
            [defaultColDef]="defaultColDef"
        />
        <app-table-pagination
            [pageNumber]="pageNumber()"
            [pageSize]="pageSize()"
            [totalResults]="totalResults()"
            (pageNumberChange)="pageNumber.set($event)"
            (pageSizeChange)="pageSize.set($event)"
        />
    </div>
  `,
})
export class TBillSettlementsComponent {
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)
  private readonly toastService = inject(ToastService)
  private readonly filterService = inject(FilterService)
  private readonly sortService = inject(TableSortService)
  private readonly modalService = inject(NgbModal)
  private readonly dashboardService = inject(DashboardService)
  private readonly tbillSettlementsService = inject(TBillSettlementsService)

  protected readonly dateTimeFormat = dateTimeFormat

  protected readonly refresh = signal(0)
  protected readonly loading = signal(false)
  protected readonly pageSize = signal(25)
  protected readonly pageNumber = signal(1)
  protected readonly totalResults = signal(0)
  protected readonly data = signal<TBillJobStatus[]>([])
  protected readonly getRowId: GetRowIdFunc<TBillJobStatus> = (data) => data.data.run_instance_uid
  protected readonly paginationParams = computed<PaginationParams>(() => ({
    pageNumber: this.pageNumber(),
    pageSize: this.pageSize(),
  }))

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Run ...', onClick: () => this.openNewRunModal() },
  ]

  protected readonly filterGroup = filterGroup

  protected readonly defaultColDef: ColDef = {
    editable: false,
    sortable: true,
    filter: true,
    minWidth: 100,
    width: 175,
    headerComponent: TableHeaderComponent,
  }

  protected readonly columnDefs: ColDef[] = [
    dateColumn('billing_month', monthOnlyFormat, 'EST'),
    dateColumn('operating_day', monthOnlyFormat, 'EST'),
    entityColumn('settlement_type'),
    entityColumn('run_status', 'run_status_name'),
    dateColumn('start_time', dateTimeWithoutTimezone),
    dateColumn('end_time', dateTimeWithoutTimezone),
    {
      headerName: headers.duration,
      field: 'duration',
      cellRenderer: ({ value }: ICellRendererParams<TBillJobStatus, number>) => formatDuration(value!),
      filter: false,
      sortable: false,
    },
    {
      headerName: 'Created by',
      field: 'created_by',
      width: 160,
      cellRenderer: ({ value }: ICellRendererParams<TBillJobStatus, string>) => formatUser(value),
      headerComponentParams: {
        filterGroup,
        modal: StringFilterModalComponent,
        modalOptions,
      },
    },
    dateColumn('create_time', dateTimeWithoutTimezone),
    actionColumn(filterGroup, {
      actions: [
        {
          name: 'View Settlement Dashboard',
          only: (d: TBillJobStatus) => ['SUCCESS'].includes(d.run_status),
          onAction: (d: TBillJobStatus) => this.goToTBillDashboard(d),
        },
        {
          name: 'Run Again',
          disabled: (d: TBillJobStatus) => !d.billing_month,
          onAction: (d: TBillJobStatus) => this.onRun(d.billing_month!, d.settlement_type),
        },
      ],
    }),
  ]

  constructor() {
    const { dispatch } = this.filterService

    dispatch(FilterService.InitFiltersAction({
      fieldGroup: filterGroup,
      fields: {
        billing_month: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: false,
            onlyMonth: true,
            meta: getMeta('billing_month'),
          },
        }, dispatch),
        operating_day: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: false,
            onlyMonth: true,
            meta: getMeta('operating_day'),
          },
        }, dispatch),
        settlement_type: new EntityFilter({
          config: {
            resource: 'tbillSettlementTypes',
            allowMultiple: true,
            allowWildcards: false,
            meta: getMeta('settlement_type'),
          },
        }, dispatch),
        run_status: new EntityFilter({
          config: {
            resource: 'runStatuses',
            allowMultiple: true,
            allowWildcards: false,
            meta: getMeta('run_status'),
          },
        }, dispatch),
        start_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: getMeta('start_time'),
          },
        }, dispatch),
        end_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: getMeta('end_time'),
          },
        }, dispatch),
        created_by: new StringFilter(
          { config: getUserFilterConfig(filterGroup, 'created_by', getHeader('created_by')) },
          dispatch,
        ),
        create_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: getMeta('create_time'),
          },
        }, dispatch),
      },
    }))

    combineLatest([
      toObservable(this.paginationParams),
      this.filterService
        .asParams(filterGroup)
        .pipe(
          tap(() => {
            if (this.pageNumber() !== 1) {
              this.pageNumber.set(1)
            }
          }),
        ),
      this.sortService.getOrder(),
      toObservable(this.refresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([pagination, filters, order]) => this.tbillSettlementsService.getTBillJobs(pagination, filters, order)),
        catchError(this.handleLoadingError('Something went wrong loading transmission settlements!')),
      )
      .subscribe({
        error: this.handleLoadingError('Something went wrong loading transmission settlements!'),
        next: (res) => {
          this.totalResults.set(res.total)
          this.data.set(res.results)
          this.loading.set(false)
        },
      })

    from(this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      filterGroup,
    ))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  protected handleLoadingError(defaultMessage: string){
    return (e: unknown) => {
      this.data.set([])
      this.loading.set(false)
      this.toastService.showError(e, defaultMessage)
      return NEVER
    }
  }

  protected async goToTBillDashboard(data: TBillJobStatus) {
    const dashboards = await firstValueFrom(this.dashboardService.get())
    const dashboardName = 'tbill_tbill_agg'
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        DashboardService.getDashboardUrlParts(dashboards, dashboardName),
        {
          queryParams: {
            [DashboardService.getWidget(dashboards, dashboardName, 'run_instance_uid')]: data.run_instance_uid,
            [DashboardService.getWidget(dashboards, dashboardName, 'settlement_type')]: data.settlement_type,
          },
        },
      ),
    )
    window.open(url, '_blank')
  }

  protected async onRun(billingMonth: Date, settlementType: string) {
    this.tbillSettlementsService.runTBill(billingMonth, settlementType)
      .subscribe({
        error: () => {
          this.toastService.show({ type: 'error', message: 'Something went wrong starting this settlement run!' })
        },
        complete: () => {
          this.modalService.dismissAll()
          this.toastService.show({ type: 'success', message: 'Request successful!' })
          this.refresh.update(v => v + 1)
        },
      })
  }

  protected openNewRunModal() {
    openModal(this.modalService, CreateSettlementRunModalComponent, { centered: true }).init(
      'tbill',
      ({ target, settlementType }) => this.onRun(target, settlementType),
    )
  }
}